import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { ErrorState, LoadingSpinner } from '../../components/common';
import { migrationService, type CutoverStatus, type ScopeView } from '../../services/migration';

const PHASE_TEXT: Record<NonNullable<CutoverStatus['phase']>, string> = {
  planned: 'Preparing (nothing has changed yet)',
  'detect-live-incumbent-alerting': 'Detect is live, but LogicMonitor is still alerting for some devices',
  complete: 'Cut over: Detect alone pages these devices',
  reversing: 'Reversing: restoring LogicMonitor’s alerting; Detect stays live until every device is confirmed',
  reversed: 'Reversed: LogicMonitor alerts again; Detect is silent',
};

/** Cutover and reverse: the only actions that write to LogicMonitor. Typed confirmation, retryable. */
export function CutoverPanel({ scope, onChanged }: { scope: ScopeView; onChanged: () => void | Promise<void> }) {
  const { tenantId } = useTenant();
  const [status, setStatus] = useState<CutoverStatus | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [mode, setMode] = useState<'cutover' | 'reverse' | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setStatus(await migrationService.cutoverStatus(tenantId, scope.id));
    } catch (err) {
      setError(err);
    }
  }, [tenantId, scope.id]);
  useEffect(() => void load(), [load, scope.stage]);

  const act = async (kind: 'cutover' | 'reverse', typed: string) => {
    if (!tenantId) return;
    setBusy(true);
    setError(null);
    try {
      setStatus(await (kind === 'cutover' ? migrationService.cutover : migrationService.reverse)(tenantId, scope.id, typed));
      setMode(null);
      setConfirm('');
      await onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode) void act(mode, confirm);
  };

  if (!status && error === null) return <LoadingSpinner />;
  const live = scope.stage === 'live';
  const phase = status?.phase ?? null;
  const unfinished = phase === 'detect-live-incumbent-alerting' || phase === 'reversing';
  const counts = status?.counts;
  return (
    <details open className="card" aria-label="Cutover">
      <summary><h3 style={{ display: 'inline' }}>Cutover</h3></summary>
      <p>
        Cutover switches Detect on <strong>first</strong>, then switches LogicMonitor’s alerting off for these devices and checks each change.
        There is never a moment with nobody paged. If something fails after Detect is live, it stays live (two pagers is noisy, zero is not) and you retry.
      </p>
      {phase && <p role="status"><strong>{PHASE_TEXT[phase]}</strong></p>}
      {counts && (
        <p className="text-muted">
          {counts.total} device(s): {counts.switched ?? 0} switched, {counts['already-disabled'] ?? 0} already disabled in LogicMonitor,{' '}
          {counts.skipped ?? 0} not in LogicMonitor, {counts.restored ?? 0} restored, {counts.failed ?? 0} failed, {counts.pending ?? 0} pending.
        </p>
      )}
      {status?.note && <p>{status.note}</p>}
      {status?.failures && status.failures.length > 0 && (
        <ul aria-label="Failures">
          {status.failures.map((f) => <li key={f.lmDeviceId}>LogicMonitor device {f.lmDeviceId}: {f.error}</li>)}
        </ul>
      )}
      {error !== null && <ErrorState error={error} />}

      {!mode && (
        <div className="page-actions">
          {!live && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setMode('cutover')}>
              Cut over…
            </button>
          )}
          {live && unfinished && phase === 'detect-live-incumbent-alerting' && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setMode('cutover')}>Retry cutover…</button>
          )}
          {live && (
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setMode('reverse')}>
              {phase === 'reversing' ? 'Continue reverse…' : 'Reverse…'}
            </button>
          )}
        </div>
      )}
      {mode && (
        <form onSubmit={submit} aria-label={mode === 'cutover' ? 'Confirm cutover' : 'Confirm reverse'}>
          <p className="notice">
            {mode === 'cutover'
              ? 'This writes to LogicMonitor: it switches off alerting for every imported device in this scope (needs the write credential).'
              : 'This writes to LogicMonitor: it switches alerting back on for the devices Detect switched off, then returns Detect to silent.'}
          </p>
          <div className="form-field">
            <label htmlFor={`cut-confirm-${scope.id}`}>Type the scope name “{scope.name}” to confirm</label>
            <input id={`cut-confirm-${scope.id}`} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />
          </div>
          <div className="page-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || confirm !== scope.name}>
              {mode === 'cutover' ? 'Cut over' : 'Reverse'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => { setMode(null); setConfirm(''); }}>Cancel</button>
          </div>
        </form>
      )}
    </details>
  );
}
